import axios, { type AxiosRequestConfig, isAxiosError } from "axios";
import { useAuthStore } from "../stores/useAuthStore";
import { refreshAccessToken } from "./authApi";

export class ApiError extends Error {
  status: number;
  errorCode?: number;
  constructor(status: number, message: string, errorCode?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
  }
}

/**
 * Pull message + errorCode out of the API's structured error envelope
 * ({ message, statusCode, errorCode }). Falls back to the raw string body
 * or the HTTP status text when the body isn't the expected shape.
 */
function toApiError(
  status: number,
  data: unknown,
  statusText: string,
): ApiError {
  if (typeof data === "string") return new ApiError(status, data);
  if (data && typeof data === "object") {
    const body = data as { message?: unknown; errorCode?: unknown };
    const message =
      typeof body.message === "string" ? body.message : statusText;
    const errorCode =
      typeof body.errorCode === "number" ? body.errorCode : undefined;
    return new ApiError(status, message, errorCode);
  }
  return new ApiError(status, statusText);
}

export const apiClient = axios.create({
  baseURL: (import.meta.env.VITE_API_URL as string | undefined) ?? "",
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;

/**
 * Single-flight session refresh shared by every caller (axios interceptor,
 * SSE stream). The refresh cookie rotates on use, so concurrent refreshes
 * would make the loser 401. A dead session clears the token and goes to /login.
 */
export function refreshSession(): Promise<string> {
  refreshPromise ??= (async () => {
    try {
      const { accessToken } = await refreshAccessToken();
      useAuthStore.getState().setAccessToken(accessToken);
      return accessToken;
    } catch (err: unknown) {
      useAuthStore.getState().clearToken();
      window.location.href = "/login";
      throw err;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!isAxiosError(error) || !error.config) throw error;

    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const accessToken = await refreshSession();
      if (originalRequest.headers) {
        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      return apiClient(originalRequest);
    }

    if (isAxiosError(error) && error.response) {
      throw toApiError(
        error.response.status,
        error.response.data,
        error.response.statusText,
      );
    }
    throw error;
  },
);

export async function request<T>(
  path: string,
  options?: AxiosRequestConfig,
): Promise<T> {
  try {
    const res = await apiClient.request<T>({ url: path, ...options });
    return res.data;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (isAxiosError(err) && err.response) {
      throw toApiError(
        err.response.status,
        err.response.data,
        err.response.statusText,
      );
    }
    throw err;
  }
}
